import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import Popper from '@di-shared/features/common/Popper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {DrivingInfoDistanceProps} from '@di-shared/features/history/driving-info/DrivingInfoDistance/index';
import useDrivingInfoEu, {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import {mapValueToRange} from '@di-shared/utils/graph';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {addDate, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';

export const DRIVING_DISTANCE_GRAPH_EU = {
  MIN_VALUE: 0,
  MAX_HEIGHT: 90,
  MIN_HEIGHT: 6,
  AREA_FULL: 100,
  AREA_HALF: 50,
  LENGTH: 3,
};

export default function ({vin, selectedDate}: DrivingInfoDistanceProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoDistanceProps) {
  const {drivingData} = useDrivingInfoEu({vin, selectedDate});

  // 가입 당월 화면
  if (drivingData.isCcsAgreedThisMonth) {
    return <Agree vin={vin} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function Agree({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const {drivingData, currentDrivingDistance} = useDrivingInfoEu({vin});

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('msg_mytrips_distance_01', {
            month: CldrDatetimeFormatter.format(today, 'MMM'),
            n: isDistanceLessThan(currentDrivingDistance.totalDistance)
              ? t('imsi_less_distance_$unit', {
                  unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                })
              : formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance)) +
                convertDistanceSymbol(currentDrivingDistance.totalDistance),
          })}
        </h4>
        <DrivingInfoDistanceTooltip />
      </div>
      <ul className="statistics_summary_list type_center">
        <li
          className="statistics_summary_item"
          role="text"
        >
          <span className="statistics_summary_key label_large_r">{t('ctn_mytirps_this_month')}</span>
          <span className="statistics_summary_val">
            {isDistanceLessThan(currentDrivingDistance.totalDistance) ? (
              <span className="title_small_b">
                {t('imsi_less_distance_$unit', {
                  unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                })}
              </span>
            ) : (
              <>
                <span className="title_small_b">
                  {formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}
                </span>
                <span className="statistics_summary_unit label_small_b">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
              </>
            )}
          </span>
        </li>
        <li
          className="statistics_summary_item"
          role="text"
        >
          <span className="statistics_summary_key label_large_r">{t('ctn_mytrips_cumulative_distance')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{formatNumberWithLimitAndCommas(convertDistanceValue(drivingData.odometer))}</span>
            <span className="statistics_summary_unit label_small_b">{convertDistanceSymbol(drivingData.odometer)}</span>
          </span>
        </li>
      </ul>
      <div className="driving_banner">
        <a
          role="button"
          className="driving_banner_box"
        >
          <span className="driving_banner_txtbox">
            <span
              className="driving_banner_desc body_small_r"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('ctn_mytirps_this_month_banner_%month', {month: CldrDatetimeFormatter.format(addDate(today, {months: 1}), 'MMM')})}
            </span>
          </span>
          <span className="driving_banner_imgbox">
            <ImageSelector
              className="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_calendar_eu.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_calendar_eu.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_calendar_eu.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_calendar_eu.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_calendar_eu.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_calendar_eu.png')}
            />
          </span>
        </a>
      </div>
    </div>
  );
}

function Default({vin, selectedDate}: DrivingInfoDistanceProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const {drivingData, verifiedDrivingDistances, currentDrivingDistance, prevDrivingDistance} = useDrivingInfoEu({
    vin,
    selectedDate,
  });
  const [selectedBarGraph, setSelectedBarGraph] = useState(getYYYYMMFormat(selectedDate));
  const isDrivingDistanceExist = drivingData.avgDistance !== 0;
  const isPrevDistanceExist = !!prevDrivingDistance;
  const distanceDiff = isPrevDistanceExist ? currentDrivingDistance.totalDistance - prevDrivingDistance.totalDistance : 0;
  const isDistanceDiffUp = isPrevDistanceExist ? distanceDiff >= 0 : true;

  const maxDistance = Math.max(drivingData.avgDistance, ...verifiedDrivingDistances.map((driving) => driving.totalDistance));
  const averageDistanceHeight = isDrivingDistanceExist
    ? DRIVING_DISTANCE_GRAPH_EU.AREA_FULL -
      mapValueToRange({
        value: drivingData.avgDistance,
        minValue: DRIVING_DISTANCE_GRAPH_EU.MIN_VALUE,
        maxValue: maxDistance,
        minRange: DRIVING_DISTANCE_GRAPH_EU.MIN_HEIGHT,
        maxRange: DRIVING_DISTANCE_GRAPH_EU.MAX_HEIGHT,
      })
    : DRIVING_DISTANCE_GRAPH_EU.AREA_HALF;

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('msg_mytrips_distance_01', {
            month: CldrDatetimeFormatter.format(selectedDate, 'MMM'),
            n: isDistanceLessThan(currentDrivingDistance.totalDistance)
              ? t('imsi_less_distance_$unit', {
                  unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                })
              : formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance)) +
                convertDistanceSymbol(currentDrivingDistance.totalDistance),
          })}
        </h4>
        <DrivingInfoDistanceTooltip />
      </div>
      {isDrivingDistanceExist && isPrevDistanceExist && (
        <div
          className="statistics_compare"
          role="text"
          aria-label={`${isDistanceDiffUp ? t('ctn_mytrips_distance_rise') : t('ctn_mytrips_distance_fall')} ${formatNumberWithLimitAndCommas(convertDistanceValue(Math.abs(distanceDiff)))}${convertDistanceSymbol(Math.abs(distanceDiff))} ${t('msg_mytrips_compared')}`}
        >
          <span
            className={clsx('statistics_compare_val', {
              type_up: isDistanceDiffUp,
              type_down: !isDistanceDiffUp,
            })}
          >
            <span className={clsx('ic', {ic_di_count_up: isDistanceDiffUp, ic_di_count_down: !isDistanceDiffUp})} />
            <span className="label_medium_b">
              {formatNumberWithLimitAndCommas(convertDistanceValue(Math.abs(distanceDiff)))}
              {convertDistanceSymbol(Math.abs(distanceDiff))}
            </span>
          </span>
          <span className="statistics_compare_key label_medium_r">{t('msg_mytrips_compared')}</span>
        </div>
      )}
      <ul className="statistics_summary_list type_center">
        <li
          className="statistics_summary_item"
          role="text"
        >
          <span className="statistics_summary_key label_large_r">{t('ctn_mytirps_this_month')}</span>
          <span className="statistics_summary_val">
            {isDistanceLessThan(currentDrivingDistance.totalDistance) ? (
              <span className="title_small_b">
                {t('imsi_less_distance_$unit', {
                  unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                })}
              </span>
            ) : (
              <>
                <span className="title_small_b">
                  {formatNumberWithLimitAndCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}
                </span>
                <span className="statistics_summary_unit label_small_b">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
              </>
            )}
          </span>
        </li>
        <li
          className="statistics_summary_item"
          role="text"
        >
          <span className="statistics_summary_key label_large_r">{t('ctn_mytrips_cumulative_distance')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{formatNumberWithLimitAndCommas(convertDistanceValue(drivingData.odometer))}</span>
            <span className="statistics_summary_unit label_small_b">{convertDistanceSymbol(drivingData.odometer)}</span>
          </span>
        </li>
      </ul>
      <div
        className="statistics_graph_area distance_statistics"
        aria-hidden="true"
        tabIndex={-1}
      >
        <div className="bar_graph">
          {verifiedDrivingDistances.length < DRIVING_DISTANCE_GRAPH_EU.LENGTH &&
            Array.from({length: DRIVING_DISTANCE_GRAPH_EU.LENGTH - verifiedDrivingDistances.length}).map((_, i) => {
              const legendDate = subDate(verifiedDrivingDistances[0].verifiedDate, {
                months: DRIVING_DISTANCE_GRAPH_EU.LENGTH - verifiedDrivingDistances.length - i,
              });
              const isSelectedMonth = getYYYYMMFormat(legendDate) === selectedBarGraph;
              return (
                <div
                  key={getYYYYMMFormat(legendDate)}
                  className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                  onClick={() => setSelectedBarGraph(getYYYYMMFormat(legendDate))}
                >
                  {isSelectedMonth && <span className="speech_bubble">-</span>}
                  <span
                    className="graph_bar"
                    style={{height: '0px'}}
                  />
                  <span className="graph_title">{CldrDatetimeFormatter.format(legendDate, 'MMM')}</span>
                </div>
              );
            })}
          {verifiedDrivingDistances.map((driving) => {
            const isSelectedMonth = driving.date === selectedBarGraph;
            const graphHeight = mapValueToRange({
              value: driving.totalDistance,
              minValue: DRIVING_DISTANCE_GRAPH_EU.MIN_VALUE,
              maxValue: maxDistance,
              minRange: DRIVING_DISTANCE_GRAPH_EU.MIN_HEIGHT,
              maxRange: DRIVING_DISTANCE_GRAPH_EU.MAX_HEIGHT,
            });
            return (
              <div
                key={driving.date}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                onClick={() => setSelectedBarGraph(driving.date)}
              >
                {isSelectedMonth && (
                  <span className="speech_bubble">
                    {isDistanceLessThan(driving.totalDistance)
                      ? t('imsi_less_distance_$unit', {
                          unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                        })
                      : formatNumberWithLimitAndCommas(convertDistanceValue(driving.totalDistance))}
                  </span>
                )}
                <span
                  className="graph_bar"
                  style={{height: `${graphHeight}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(driving.verifiedDate, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${averageDistanceHeight}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('ctn_mytrips_average')}</span>
              <span className="average_value">{formatNumberWithLimitAndCommas(convertDistanceValue(drivingData.avgDistance))}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_EU.LENGTH).keys()).reverse();

  return (
    <div
      className="section_container skeleton"
      aria-hidden="true"
    >
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('msg_mytrips_distance_01', {
            month: CldrDatetimeFormatter.format(today, 'MMM'),
            n: ERROR.DASH + defaultDistanceSymbol,
          })}
        </h4>
      </div>
      <ul className="statistics_summary_list type_center">
        <li className="statistics_summary_item">
          <span className="statistics_summary_key label_large_r">{t('ctn_mytirps_this_month')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{ERROR.DASH}</span>
            <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
          </span>
        </li>
        <li className="statistics_summary_item">
          <span className="statistics_summary_key label_large_r">{t('ctn_mytrips_cumulative_distance')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{ERROR.DASH}</span>
            <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
          </span>
        </li>
      </ul>
      <div
        className="statistics_graph_area distance_statistics"
        aria-hidden="true"
        tabIndex={-1}
      >
        <div className="bar_graph">
          {errorDrivingDistances.map((month) => {
            const legendMonth = subDate(today, {months: month});
            const isSelectedMonth = isSameYearAndMonth(legendMonth, today);
            return (
              <div
                key={month}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
              >
                {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                <span
                  className="graph_bar"
                  style={{height: `${DRIVING_DISTANCE_GRAPH_EU.MIN_HEIGHT}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(legendMonth, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${DRIVING_DISTANCE_GRAPH_EU.AREA_HALF}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('ctn_mytrips_average')}</span>
              <span className="average_value">{ERROR.DASH}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const startDate = startDateOfMonth(selectedDate);
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_EU.LENGTH).keys()).reverse();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('msg_mytrips_distance_01', {
            month: CldrDatetimeFormatter.format(startDate, 'MMM'),
            n: ERROR.DASH + defaultDistanceSymbol,
          })}
        </h4>
        <DrivingInfoDistanceTooltip />
      </div>

      <div
        className="statistics_compare"
        role="text"
        aria-label={`${t('ctn_mytrips_distance_rise')} ${ERROR.DASH}${defaultDistanceSymbol} ${t('msg_mytrips_compared')}`}
      >
        <span className="statistics_compare_val type_up">
          <span className="ic ic_di_count_up" />
          <span className="label_medium_b">
            {ERROR.DASH}
            {defaultDistanceSymbol}
          </span>
        </span>
        <span className="statistics_compare_key label_medium_r">{t('msg_mytrips_compared')}</span>
      </div>

      <ul className="statistics_summary_list type_center">
        <li
          className="statistics_summary_item"
          role="text"
        >
          <span className="statistics_summary_key label_large_r">{t('ctn_mytirps_this_month')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{ERROR.DASH}</span>
            <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
          </span>
        </li>
        <li
          className="statistics_summary_item"
          role="text"
        >
          <span className="statistics_summary_key label_large_r">{t('ctn_mytrips_cumulative_distance')}</span>
          <span className="statistics_summary_val">
            <span className="title_small_b">{ERROR.DASH}</span>
            <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
          </span>
        </li>
      </ul>
      <div
        className="statistics_graph_area distance_statistics"
        aria-hidden="true"
        tabIndex={-1}
      >
        <div className="bar_graph">
          {errorDrivingDistances.map((month) => {
            const legendMonth = subDate(startDate, {months: month});
            const isSelectedMonth = isSameYearAndMonth(legendMonth, startDate);
            return (
              <div
                key={month}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
              >
                {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                <span
                  className="graph_bar"
                  style={{height: `${DRIVING_DISTANCE_GRAPH_EU.MIN_HEIGHT}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(legendMonth, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${DRIVING_DISTANCE_GRAPH_EU.AREA_HALF}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('ctn_mytrips_average')}</span>
              <span className="average_value">{ERROR.DASH}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingInfoDistanceTooltip() {
  const {t} = useInferableTranslation();
  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={3}
      tooltip={
        <div
          role="tooltip"
          tabIndex={0}
        >
          <p
            className="tooltip_text"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_monthly_distance_tooltip_eu')}
          </p>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          className="btn_tooltip"
          aria-expanded="false"
          type="button"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          />
        </button>
      </div>
    </Popper>
  );
}
